import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import { Select, MenuItem, FormControl, InputLabel } from "@material-ui/core";
import AppMenu from "../../menu";
import TablesData from "../globalTable";
import TableItem from "../globalTable/tableItem";
import { SearchOnly } from "../globalTable/searchandfilter";
import HorizontalTabs from "../../tabs/horizontalTabs";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../common/Loader";
import { fetchProviders, getProviders } from "../../../store/planProviders";
import { axiosInstance } from "../../../utils/axiosInstance";
import ReusableModal from "../modals/ReusableModal";
import useDebounce from "../../../utils/useDebounce";

const tabLabels = ["Principal Directory", "Dependent Directory"];

export default function HealthPlanProvidersTable() {
  const history = useHistory();
  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState("name"); // Default to name
  const [selected, setSelected] = useState([]);
  const dispatch = useDispatch();
  const othersData = useSelector(getProviders);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLists, setPageLists] = useState(10);
  const { activetab } = useParams();
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    mode: null,
    providerData: null,
  });

  // Debounce search and searchField
  const debouncedSearch = useDebounce(search, 300);
  const debouncedSearchField = useDebounce(searchField, 300);

  const tabNameToIndex = {
    0: "principal-directory",
    1: "dependent-directory",
  };

  const indexToTabName = {
    "principal-directory": 0,
    "dependent-directory": 1,
  };

  const [tabIndex, setTabIndex] = useState(indexToTabName[activetab]);

  const onChangeTabIndex = (e, newValue) => {
    history.push(
      `/hmo/dashboard/health-plan-provider/${tabNameToIndex[newValue]}`
    );
    setTabIndex(newValue);
  };

  useEffect(() => {
    dispatch(
      fetchProviders(
        pageLists,
        pageNumber,
        debouncedSearchField,
        debouncedSearch
      )
    );
  }, [dispatch, pageNumber, pageLists, debouncedSearchField, debouncedSearch]);

  const incrementPageNumber = () => {
    setPageNumber((prev) => prev + 1);
  };

  const decrementPageNumber = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const handleSelectNumber = ({ target }) => {
    setPageLists(Number(target.value));
    setPageNumber(1); // Reset to first page
  };

  const handleSearchChange = (e) => {
    e.preventDefault();
    setSearch(e.target.value);
    setPageNumber(1); // Reset to first page
  };

  const handleSearchFieldChange = (e) => {
    setSearchField(e.target.value);
    setPageNumber(1); // Reset to first page
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else if (selectedIndex === 0) {
      newSelected = selected.slice(1);
    } else if (selectedIndex === selected.length - 1) {
      newSelected = selected.slice(0, -1);
    } else {
      newSelected = [
        ...selected.slice(0, selectedIndex),
        ...selected.slice(selectedIndex + 1),
      ];
    }

    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleSelectAllClick = () => {
    const newSelecteds = othersData?.ProvidersList?.map((n) => n.id) || [];
    setSelected(newSelecteds);
  };

  const handleDeletePlanProvider = async (id) => {
    try {
      const response = await axiosInstance.delete(`/v1/admin/providers/${id}`);
      if (response?.message) {
        dispatch(
          fetchProviders(
            pageLists,
            pageNumber,
            debouncedSearchField,
            debouncedSearch
          )
        );
      }
    } catch (error) {
      console.error("Error deleting health plan provider:", error);
      throw error;
    }
  };

  const handleEditPlanProvider = async (id, data) => {
    try {
      const response = await axiosInstance.put(
        `/v1/admin/providers/${id}`,
        data
      );
      if (response) {
        dispatch(
          fetchProviders(
            pageLists,
            pageNumber,
            debouncedSearchField,
            debouncedSearch
          )
        );
      }
    } catch (error) {
      console.error("Error updating health plan provider:", error);
      throw error;
    }
  };

  const openModal = (mode, providerData) => {
    setModalConfig({
      isOpen: true,
      mode,
      providerData,
    });
  };

  const closeModal = () => {
    setModalConfig({ isOpen: false, mode: null, providerData: null });
  };

  const tabBodyData = othersData?.ProvidersList?.map((item, i) => {
    const isItemSelected = isSelected(item.id);
    const labelId = `enhanced-table-checkbox-${i}`;
    return (
      <TablesData
        labelId={labelId}
        handleItemClick={(event) => handleClick(event, item.id)}
        aria-checked={isItemSelected}
        isItemSelected={isItemSelected}
        key={i}
        checkBoxes
        image
        imgInitials={item?.name?.slice(0, 2)}
        enroleeName={item?.name}
        enroleeId={item?.id}
        listItem={[
          item?.location,
          <span
            style={{
              wordBreak: "break-word",
              whiteSpace: "normal",
              display: "block",
              maxWidth: 200,
            }}
          >
            {item?.address}
          </span>,
          item?.health_plan_id === 1
            ? "EXCLUSIVE"
            : item?.health_plan_id === 2
            ? "PREMIUM"
            : item?.health_plan_id === 3
            ? "SHIELD"
            : item?.health_plan_id === 4
            ? "GUARD"
            : item?.health_plan_id === 5
            ? "BASIC"
            : "UNKNOWN",
        ]}
        action={
          <AppMenu
            menu_name_or_icon={<MoreHorizIcon />}
            item_names={[
              {
                "View More": () => {
                  history.push(
                    `/hmo/dashboard/health-plan-provider/${item.id}`
                  );
                },
              },
              {
                Edit: () => {
                  openModal("edit", item);
                },
              },
              {
                Delete: () => {
                  openModal("delete", item);
                },
              },
            ]}
          />
        }
      />
    );
  });

  return (
    <>
      <div
        style={{
          display: "flex",
          marginBottom: "2rem",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <FormControl style={{ minWidth: 120 }}>
            <InputLabel id="search-field-label">Search By</InputLabel>
            <Select
              labelId="search-field-label"
              value={searchField}
              onChange={handleSearchFieldChange}
            >
              <MenuItem value="name">Hospital Name</MenuItem>
              <MenuItem value="location">Location</MenuItem>
              <MenuItem value="address">Address</MenuItem>
            </Select>
          </FormControl>
          <SearchOnly handleSearch={handleSearchChange} searchWord={search} />
        </div>
        <HorizontalTabs
          tabData={tabLabels}
          tabIndex={tabIndex}
          onChangeTabIndex={onChangeTabIndex}
        />
      </div>
      <Loader visible={othersData?.loading} />
      <ReusableModal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        mode={modalConfig.mode}
        providerData={modalConfig.providerData}
        onSubmit={handleEditPlanProvider}
        onDelete={handleDeletePlanProvider}
      />
      {tabBodyData?.length === 0 && !othersData?.loading ? (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          No providers found for the given search.
        </div>
      ) : (
        <TableItem
          userType="Provider"
          checkBoxes
          tabHeadLabel={["Hospital Name", "Location", "Address", "Plan Name"]}
          action
          tableSize
          tabToolBar
          tabHeadCheck
          numSelectedItem={selected?.length || 0}
          rowCount={othersData?.ProvidersList?.length || 0}
          numSelected={selected?.length || 0}
          onSelectAllClick={handleSelectAllClick}
          currentPage={othersData?.currentPage || 1}
          toPage={othersData?.toPage || 10}
          totalPage={othersData?.total || 0}
          handleIncrement={incrementPageNumber}
          handleDecrement={decrementPageNumber}
          disabledIncrement={pageNumber >= (othersData?.lastPage || 1)}
          disabledDecrement={pageNumber <= 1}
          handleSelectPageNumber={handleSelectNumber}
          pageSelectValue={pageLists}
          tabBodyData={tabBodyData}
          tablePagination
        />
      )}
    </>
  );
}
